import { useMsal } from '@azure/msal-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet } from 'react-router-dom';
import { belongsToAGroup, useCurrentUser } from '../auth/CurrentUserProvider';
import { signOutLocally } from '../auth/signOut';
import { COMPACT_SCREEN, useMediaQuery } from '../hooks/useMediaQuery';
import { Footer } from './Footer';
import { HeaderMenu } from './HeaderMenu';
import { LanguageMenu } from './LanguageMenu';
import { BULK_ITEM, GROUPS_ITEM, NAV_ITEMS, type NavItem } from './navItems';
import { ThemeToggle } from './ThemeToggle';
import { usePageTitle } from './PageTitle';

/**
 * The application shell: Kersia's top bar, with the signed-in user beside the logo, the current page's name in
 * the middle, and the navigation and language menu on the right - the same arrangement as MyAccount.
 *
 * Under 768px the row of controls becomes one button and a menu holding all of them. Which one renders is
 * decided here rather than in CSS, so one copy of each control is in the accessibility tree rather than two
 * with one hidden, and the width is read during the first render so the right one is in the first paint.
 */
export function Layout() {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const title = usePageTitle();
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  const compact = useMediaQuery(COMPACT_SCREEN);
  const items = useNavItems();
  // This application's own sign-out, not Microsoft's: see signOut.ts.
  const signOut = () => void signOutLocally(instance);

  return (
    <>
      <header className="app-header">
        <div className="brand">
          <img src="/images/kersia_logo.svg" alt="Kersia" className="brand-logo" />
          {/* The application's own mark, beside the company's. Decorative: the name is already the h1. */}
          <img src="/favicon.svg" alt="" aria-hidden="true" className="app-icon" />
          <span className="brand-divider" aria-hidden="true" />
          <div className="user-infos">
            <span className="fw-semibold">{account?.name ?? account?.username}</span>
            <span className="text-secondary">{account?.username}</span>
          </div>
        </div>

        <div className="page-title">
          <h1>{title || t('app.name')}</h1>
        </div>

        <div className="header-actions">
          {compact ? (
            <HeaderMenu
              items={items}
              name={account?.name}
              email={account?.username}
              onSignOut={signOut}
            />
          ) : (
            <div className="actions expanded">
              <nav aria-label={t('nav.menu')} className="app-nav">
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    // Named on the link itself, because the label below is display:none under 1200px - and a
                    // display:none label is not in the accessibility tree either.
                    title={t(item.labelKey)}
                    aria-label={t(item.labelKey)}
                    className={({ isActive }) => `btn btn-primary${isActive ? ' active' : ''}`}
                  >
                    <i className={`pi ${item.icon}`} aria-hidden="true" />
                    <span className="d-none d-xl-inline ms-2">{t(item.labelKey)}</span>
                  </NavLink>
                ))}
              </nav>

              <LanguageMenu />

              <ThemeToggle />

              <button type="button" className="btn btn-danger" title={t('nav.signOut')} onClick={signOut}>
                <i className="pi pi-sign-out" aria-hidden="true" />
                <span className="d-none d-xxl-inline ms-2">{t('nav.signOut')}</span>
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="app-main">
        <Outlet />
      </main>

      <Footer />
    </>
  );
}

/**
 * The pages worth offering this person. Users and groups has nothing on it for somebody who is in no group:
 * no members to see, no codes shared with them, nothing to administer.
 *
 * Included until the answer says otherwise, so the bar stays still for the majority who are in a group, and
 * stays whole when the answer never comes.
 */
function useNavItems(): readonly NavItem[] {
  const { user } = useCurrentUser();
  const inAGroup = belongsToAGroup(user);
  const canBulkEdit = user?.canBulkEdit === true;

  return useMemo(
    () =>
      NAV_ITEMS.filter(
        (item) => (inAGroup || item.to !== GROUPS_ITEM) && (canBulkEdit || item.to !== BULK_ITEM),
      ),
    [inAGroup, canBulkEdit],
  );
}
